import { SectionCardHeader } from './section-card-header.js';
import { ClientCredit } from './client-credit.js';
import { AppointmentSale } from '../modules/sales/index.js';
import { formatPhone } from '@hue/contracts';
import {
  Button,
  Modal,
  Textarea,
  FormErrorSummary,
  formatCurrency,
  useSnackbar,
} from '@hue/design-system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Clock3, DoorOpen, Package, UserRound } from 'lucide-react';
import { useRef, useState } from 'react';
import {
  clientsApi,
  getApiError,
  schedulingApi,
  type Appointment,
  type AppointmentSession,
  type ClientChart,
} from '../lib/api.js';
import { formatDate, formatDateTime, formatHourMinute } from '../lib/time-zone.js';
import { useNow } from '../lib/use-now.js';
import {
  ClosingMaterialsSection,
  DraftStatus,
  useClosingMaterials,
} from '../modules/materials-usage/index.js';
import { ClosingProtocolSection, useClosingProtocol } from '../modules/protocols/index.js';
import { ClientConsents } from '../modules/clients/index.js';
import {
  HealthPanel,
  RecordsSidePanel,
  SessionBefore,
  SessionCheck,
  SessionOutcome,
  serviceTemplatesOf,
  useAppointmentRecords,
  type PendingOutcome,
  type RecordsPanel,
} from '../modules/records/index.js';
import {
  AppointmentCancellationPolicyCard,
  AppointmentDepositCard,
  AppointmentNoticesCard,
  AppointmentStatusPill,
} from '../modules/scheduling/index.js';
import { useCompanyScope } from './company-context.js';
import { ErrorCard, LoadingCard, RequestAlert } from './data-view.js';
import { GatedCompanyScreen } from './gated-company-screen.js';
import { PageHeader } from './page-title.js';
import { AppointmentLayoutToggle, AppointmentPhase } from './appointment-layout.js';
import { useAppointmentLayout } from './use-appointment-layout.js';

const CARD = 'rounded-2xl border border-surface-border bg-surface-card p-5 shadow-soft';

/// O atendimento (ADR 0030 e ADR 0038, adendo de 2026-09-28): uma página só, na ordem do dia —
/// antes de começar, checagem, protocolo, material, registro da sessão e a conclusão. Vive na
/// composição porque é a única camada que pode conhecer os donos ao mesmo tempo — Agenda,
/// Prontuário, Protocolos, Consumo de materiais e Clientes —, sem nenhum importar o vizinho.
export function AppointmentClosingScreen({
  appointmentId,
  onBack,
  onOpenClient,
  onOpenSale,
}: {
  appointmentId: string;
  onBack: () => void;
  onOpenClient: (clientId: string, photos?: boolean, appointmentId?: string) => void;
  /// A Nova venda do atendimento, para gerar a venda ou adicionar itens a ela (ADR 0059).
  onOpenSale: (appointmentId: string) => void;
}) {
  const { can } = useCompanyScope();
  return (
    <GatedCompanyScreen allowed={can('scheduling.read')}>
      <AppointmentClosing
        appointmentId={appointmentId}
        onBack={onBack}
        onOpenClient={onOpenClient}
        onOpenSale={onOpenSale}
      />
    </GatedCompanyScreen>
  );
}

function AppointmentClosing({
  appointmentId,
  onBack,
  onOpenClient,
  onOpenSale,
}: {
  appointmentId: string;
  onBack: () => void;
  onOpenClient: (clientId: string, photos?: boolean, appointmentId?: string) => void;
  onOpenSale: (appointmentId: string) => void;
}) {
  const { can, timeZone, today, company } = useCompanyScope();
  const { layout, choose, saving } = useAppointmentLayout();
  // Na visão de todas as unidades o material só é consultado: registrar e estornar pedem a unidade
  // do atendimento escolhida no seletor (ADR 0039).
  const consolidated =
    (company.businessUnits ?? []).length > 1 && (company.activeBusinessUnitId ?? null) === null;
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const [completing, setCompleting] = useState(false);
  const [completeError, setCompleteError] = useState<string>();
  const [reopenOpen, setReopenOpen] = useState(false);
  const now = useNow();
  const appointmentKey = ['company', 'scheduling', 'appointment', appointmentId];
  const appointment = useQuery({
    queryKey: appointmentKey,
    queryFn: () => schedulingApi.getAppointment(appointmentId),
    retry: false,
  });
  const data = appointment.data;

  const protocolEnabled = can('protocols.applications.read');
  const canReadProtocolMaterials = can('protocols.materials.read');
  const protocol = useClosingProtocol({
    appointmentId,
    serviceId: data?.serviceId,
    enabled: protocolEnabled,
    canReadMaterials: canReadProtocolMaterials,
  });
  const planned = protocolEnabled ? protocol.planned : null;
  const plannedReady =
    !protocolEnabled ||
    (!protocol.loading &&
      (protocol.chosenId === null || !canReadProtocolMaterials || protocol.planned !== null));
  const canManageProtocol = !consolidated && can('protocols.applications.manage');
  const usageEnabled = can('materials_usage.read');
  const canRegister =
    !consolidated && data?.status !== 'NO_SHOW' && can('materials_usage.register');
  const materials = useClosingMaterials({
    appointmentId,
    enabled: usageEnabled,
    planned,
    plannedReady,
    canRegister,
    timeZone,
  });

  const client = useQuery({
    queryKey: ['company', 'clients', data?.clientId],
    queryFn: () => clientsApi.get(data!.clientId),
    enabled: Boolean(data) && can('clients.read'),
    retry: false,
  });

  // O Prontuário só aparece para quem o lê; sem `records.read` a página fica como antes.
  const recordsEnabled = can('records.read');
  const canManageRecords = can('records.manage');
  const records = useAppointmentRecords(appointmentId, data?.clientId, recordsEnabled);
  const [panel, setPanel] = useState<RecordsPanel | null>(null);
  const [noShowOpen, setNoShowOpen] = useState(false);
  const [noShowReason, setNoShowReason] = useState('');
  const [noShowBusy, setNoShowBusy] = useState(false);
  const [noShowError, setNoShowError] = useState<string>();
  const pendingOutcome = useRef<PendingOutcome | null>(null);
  const refreshRecords = () => queryClient.invalidateQueries({ queryKey: ['company', 'records'] });
  const updateRecord = (templateId: string | null) =>
    templateId ? () => setPanel({ kind: 'edit', templateId }) : null;

  if (appointment.isLoading) return <LoadingCard label="Carregando atendimento" />;
  if (appointment.error || !data) return <ErrorCard message={getApiError(appointment.error)} />;

  const completed = data.status === 'COMPLETED' || data.status === 'NO_SHOW';
  const cancelled = data.status === 'CANCELLED';
  const started = now >= Date.parse(data.startsAt);
  const canComplete = can('scheduling.complete');

  async function complete(current: Appointment) {
    setCompleteError(undefined);
    setCompleting(true);
    try {
      // O registro da sessão preenchido e não salvo grava junto; com erro, nada é concluído.
      if (pendingOutcome.current && !(await pendingOutcome.current.save())) {
        setCompleteError('Corrija o registro da sessão antes de concluir.');
        return;
      }
      // Nesta ordem (ADR 0030): o protocolo aplicado retrata o previsto que a linha de material
      // guarda; o material registrado baixa o estoque; só então o atendimento é concluído.
      if (protocolEnabled && canManageProtocol) await protocol.applyChosen();
      if (usageEnabled && canRegister && !(await materials.register())) {
        setCompleteError('O atendimento continua aberto: corrija o material e conclua de novo.');
        return;
      }
      await schedulingApi.completeAppointment(current.id);
      await queryClient.invalidateQueries({ queryKey: ['company', 'scheduling'] });
      await queryClient.invalidateQueries({ queryKey: ['company', 'prepaid'] });
      await queryClient.invalidateQueries({ queryKey: ['company', 'material-usage'] });
      if (recordsEnabled) await refreshRecords();
      snackbar.success('Atendimento concluído.');
      window.scrollTo({ top: 0 });
    } catch (cause) {
      setCompleteError(getApiError(cause));
    } finally {
      setCompleting(false);
    }
  }

  return (
    <section aria-labelledby="closing-title">
      <PageHeader
        onBack={onBack}
        trail="Operação / Agenda / Atendimento"
        id="closing-title"
        title={data.clientDisplayName}
        aside={
          recordsEnabled ? (
            <Button size="sm" variant="outline" onClick={() => setPanel({ kind: 'chart' })}>
              Linha do tempo
            </Button>
          ) : null
        }
      >
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-content-secondary">
          <AppointmentStatusPill
            status={data.status}
            requiresConfirmation={data.requiresConfirmation}
          />
          <span className="inline-flex items-center gap-1.5 tabular-nums">
            <Clock3 className="size-4 text-content-muted" />
            {formatDate(data.startsAt, timeZone)} · {formatHourMinute(data.startsAt, timeZone)}–
            {formatHourMinute(data.endsAt, timeZone)}
          </span>
          <span>
            {data.serviceDisplayName} · {formatCurrency(data.priceCents, data.currency)}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <UserRound className="size-4 text-content-muted" />
            {data.professionalDisplayName}
          </span>
          {data.roomDisplayName ? (
            <span className="inline-flex items-center gap-1.5">
              <DoorOpen className="size-4 text-content-muted" />
              {data.roomDisplayName}
            </span>
          ) : null}
        </div>
      </PageHeader>
      <div className="grid grid-cols-[minmax(0,1fr)] gap-6">
        <section aria-label="Informações comuns do atendimento" className="grid min-w-0 gap-6">
          <h2 className="text-lg font-semibold">Informações do atendimento</h2>
          <div className="grid min-w-0 items-start gap-6 lg:grid-cols-2">
            {recordsEnabled && records.chart.data ? (
              <HealthPanel chart={records.chart.data} timeZone={timeZone} compact />
            ) : null}
            <div className="rounded-2xl border border-surface-border bg-surface-card shadow-soft">
              <SectionCardHeader title="Cliente" icon={UserRound} />
              <div className="p-4">
                <p className="text-lg font-semibold">{data.clientDisplayName}</p>
                {client.data ? (
                  <dl className="mt-4 grid grid-cols-[6rem_minmax(0,1fr)] gap-x-3 gap-y-2 text-sm">
                    <dt className="text-content-muted">Celular</dt>
                    <dd className="tabular-nums">{formatPhone(client.data.phone) ?? '—'}</dd>
                    <dt className="text-content-muted">E-mail</dt>
                    <dd className="break-words">{client.data.email ?? '—'}</dd>
                    {client.data.birthDate ? (
                      <>
                        <dt className="text-content-muted">Nascimento</dt>
                        <dd className="tabular-nums">
                          {formatDate(`${client.data.birthDate}T12:00:00Z`, 'UTC')} ·{' '}
                          {ageOf(client.data.birthDate)} anos
                        </dd>
                      </>
                    ) : null}
                    <dt className="text-content-muted">Documento</dt>
                    <dd className="tabular-nums">{client.data.document ?? '—'}</dd>
                  </dl>
                ) : null}
                {client.data?.notes ? (
                  <p className="mt-4 rounded-xl border border-warning/30 bg-warning/5 p-3 text-xs text-content-secondary">
                    <strong className="text-content">Observação do cadastro:</strong>{' '}
                    {client.data.notes}
                  </p>
                ) : null}
                {can('clients.read') ? (
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-4"
                    onClick={() => onOpenClient(data.clientId)}
                  >
                    Abrir página do cliente
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
          {recordsEnabled ? (
            <Button
              variant="outline"
              className="justify-self-start"
              onClick={() => setPanel({ kind: 'chart' })}
            >
              Prontuário e fichas de anamnese
            </Button>
          ) : null}
          <AppointmentDepositCard
            appointment={data}
            timeZone={timeZone}
            today={today}
            canManage={can('scheduling.update')}
          />
          <AppointmentCancellationPolicyCard appointment={data} />
          <AppointmentNoticesCard appointmentId={data.id} canManage={can('scheduling.update')} />
          <div
            aria-label="Crédito e pagamento do atendimento"
            className="grid min-w-0 gap-6 lg:grid-cols-2 [&>*]:min-w-0 [&>:only-child]:col-span-full"
          >
            <ClientCredit
              clientId={data.clientId}
              businessUnitId={data.businessUnitId}
              prepaid={Boolean(data.prepaidBalanceId)}
            />
            {!consolidated && can('sales.read') ? (
              <AppointmentSale
                appointmentId={data.id}
                prepaid={Boolean(data.prepaidBalanceId)}
                cancelled={cancelled}
                onOpenSale={() => onOpenSale(data.id)}
                canCreate={can('sales.create')}
                canManagePayments={can('sales.payments.manage')}
                financeContracted={company.moduleKeys.includes('finance')}
                today={today}
              />
            ) : null}
            {data.prepaidBalanceId ? (
              <div className="rounded-2xl border border-surface-border bg-surface-card shadow-soft">
                <SectionCardHeader title="Sessão do pacote" icon={Package} />
                <div className="p-4">
                  <p className="mt-2 text-sm">
                    {completed
                      ? 'A regra do pacote foi aplicada a este atendimento.'
                      : cancelled
                        ? 'A reserva foi encerrada conforme a antecedência do cancelamento.'
                        : 'Uma sessão está reservada para este atendimento. Concluir transforma a reserva em uso.'}
                  </p>
                  {can('prepaid_balances.read') ? (
                    <Button
                      className="mt-3"
                      variant="outline"
                      onClick={() => onOpenClient(data.clientId)}
                    >
                      Consultar saldos e histórico do cliente
                    </Button>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        </section>
        {!cancelled ? (
          <AppointmentLayoutToggle
            saving={saving}
            layout={layout}
            onChange={(next) => void choose(next)}
          />
        ) : null}
        {cancelled ? (
          <div className={CARD}>
            <p className="text-sm text-content-muted">
              Atendimento cancelado não é fechado.
              {data.cancellationReason ? ` Motivo: ${data.cancellationReason}.` : ''}
            </p>
          </div>
        ) : (
          <div
            className={`grid min-w-0 items-start gap-6 ${layout === 'columns' ? 'xl:grid-cols-2' : ''}`}
          >
            <AppointmentPhase phase="before">
              {!recordsEnabled ? (
                <p className="text-sm text-content-muted">
                  Confira as informações do atendimento antes de começar.
                </p>
              ) : null}
              {recordsEnabled ? (
                records.session.error ? (
                  <ErrorCard message={getApiError(records.session.error)} />
                ) : records.session.data && records.chart.data ? (
                  <>
                    <SessionBefore
                      session={records.session.data}
                      chart={records.chart.data}
                      timeZone={timeZone}
                      canManage={canManageRecords}
                      onPanel={setPanel}
                    />
                    <SessionCheck
                      key={records.session.data.session?.check?.recordedAt ?? 'new'}
                      session={records.session.data}
                      canManage={canManageRecords}
                      timeZone={timeZone}
                      today={today}
                      onSaved={refreshRecords}
                      onUpdateRecord={updateRecord(
                        serviceRecordTemplate(records.chart.data, records.session.data),
                      )}
                    />
                  </>
                ) : (
                  <LoadingCard label="Carregando prontuário" />
                )
              ) : null}
            </AppointmentPhase>
            <AppointmentPhase phase="after">
              {protocolEnabled ? (
                <div className={CARD}>
                  <ClosingProtocolSection
                    protocol={protocol}
                    canManage={canManageProtocol}
                    locked={completed}
                    timeZone={timeZone}
                    onChosen={(next) => materials.resetTo(next)}
                  />
                </div>
              ) : null}
              {usageEnabled ? (
                <div className={CARD}>
                  <ClosingMaterialsSection
                    materials={materials}
                    planned={planned}
                    canRegister={canRegister}
                    canReverse={!consolidated && can('materials_usage.reverse')}
                    completed={completed}
                    timeZone={timeZone}
                  />
                </div>
              ) : null}
              {!protocolEnabled && !usageEnabled && !recordsEnabled ? (
                <div className={CARD}>
                  <p className="text-sm text-content-muted">
                    Sem protocolo nem material a registrar, o fechamento é a conclusão do
                    atendimento.
                  </p>
                </div>
              ) : null}
              {recordsEnabled && records.session.data ? (
                <SessionOutcome
                  key={`${records.session.data.session?.outcome?.recordedAt ?? 'new'}-${String(records.session.data.locked)}`}
                  session={records.session.data}
                  canManage={canManageRecords}
                  timeZone={timeZone}
                  onSaved={refreshRecords}
                  pendingRef={pendingOutcome}
                />
              ) : null}
            </AppointmentPhase>
          </div>
        )}

        <Modal
          open={noShowOpen}
          onOpenChange={setNoShowOpen}
          title="Marcar faltou"
          footer={
            <>
              <FormErrorSummary form="appointment-no-show" />
              <Button
                type="submit"
                form="appointment-no-show"
                loading={noShowBusy}
                onClick={() => {
                  void (async () => {
                    if (noShowReason.trim().length < 3) {
                      setNoShowError('Informe o motivo da falta.');
                      return;
                    }
                    setNoShowBusy(true);
                    setNoShowError(undefined);
                    try {
                      await schedulingApi.noShow(data.id, noShowReason);
                      await queryClient.invalidateQueries({ queryKey: ['company', 'scheduling'] });
                      await queryClient.invalidateQueries({ queryKey: ['company', 'prepaid'] });
                      await refreshRecords();
                      setNoShowOpen(false);
                      snackbar.success('Falta registrada conforme a regra do pacote.');
                    } catch (failure) {
                      setNoShowError(getApiError(failure));
                    } finally {
                      setNoShowBusy(false);
                    }
                  })();
                }}
              >
                Confirmar falta
              </Button>
            </>
          }
        >
          <form id="appointment-no-show" onSubmit={(event) => event.preventDefault()}>
            <p className="mb-4 text-sm">
              A sessão reservada será descontada somente se a regra do pacote determinar consumo por
              falta.
            </p>
            <Textarea
              label="Motivo da falta"
              aria-invalid={!!noShowError && noShowReason.trim().length < 3}
              value={noShowReason}
              onChange={(event) => setNoShowReason(event.target.value)}
            />
            {noShowError ? <RequestAlert message={noShowError} /> : null}
          </form>
        </Modal>
        {cancelled ? null : (
          <div className="sticky bottom-0 z-20 -mx-5 border-t border-surface-border bg-surface/95 px-5 py-3 backdrop-blur sm:-mx-8 sm:px-8">
            <div className="flex flex-wrap items-center justify-end gap-3">
              <div className="mr-auto flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1 text-xs text-content-secondary">
                {completed ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Check className="size-4 text-positive" />
                    {data.status === 'NO_SHOW' ? 'Faltou' : 'Concluído'}
                    {data.completedByDisplayName ? ` por ${data.completedByDisplayName}` : ''}
                    {data.completedAt ? ` em ${formatDateTime(data.completedAt, timeZone)}` : ''}
                  </span>
                ) : data.requiresConfirmation && data.status === 'SCHEDULED' ? (
                  <span className="text-warning">
                    Confirme a solicitação na Agenda antes de atender.
                  </span>
                ) : !started ? (
                  <span className="text-warning">
                    Concluir fica disponível a partir das{' '}
                    {formatHourMinute(data.startsAt, timeZone)}.
                  </span>
                ) : null}
                {usageEnabled && canRegister ? <DraftStatus materials={materials} /> : null}
              </div>
              {completed ? (
                canComplete ? (
                  <Button variant="outline" onClick={() => setReopenOpen(true)}>
                    Reabrir atendimento
                  </Button>
                ) : null
              ) : (
                <>
                  {/* No celular o "Voltar" do topo basta: o rodapé fixo fica só com a ação. */}
                  <Button variant="outline" className="hidden sm:inline-flex" onClick={onBack}>
                    Voltar
                  </Button>
                  {canComplete &&
                  started &&
                  !(data.requiresConfirmation && data.status === 'SCHEDULED') ? (
                    <Button
                      variant="outline"
                      disabled={completing}
                      onClick={() => setNoShowOpen(true)}
                    >
                      Marcar faltou
                    </Button>
                  ) : null}
                  {canComplete ? (
                    <Button
                      className="w-full sm:w-auto"
                      loading={completing}
                      disabled={
                        !started || (data.requiresConfirmation && data.status === 'SCHEDULED')
                      }
                      onClick={() => void complete(data)}
                    >
                      <Check className="size-4" />
                      Concluir atendimento
                    </Button>
                  ) : null}
                </>
              )}
            </div>
            {completeError ? (
              <div className="mt-3">
                <RequestAlert message={completeError} />
              </div>
            ) : null}
          </div>
        )}

        {recordsEnabled ? (
          <RecordsSidePanel
            panel={panel}
            clientId={data.clientId}
            timeZone={timeZone}
            today={today}
            canManage={canManageRecords && !completed}
            canManagePhotos={can('records.manage')}
            consents={
              client.data ? (
                <ClientConsents
                  client={client.data}
                  canUpdate={can('clients.update')}
                  timeZone={timeZone}
                />
              ) : undefined
            }
            onClose={() => setPanel(null)}
            onPanel={setPanel}
            onOpenClientPage={(photos, appointmentId) =>
              onOpenClient(data.clientId, photos, appointmentId)
            }
          />
        ) : null}

        {reopenOpen ? (
          <ReopenDialog
            appointment={data}
            onOpenChange={setReopenOpen}
            onReopened={async () => {
              await queryClient.invalidateQueries({ queryKey: ['company', 'scheduling'] });
              await queryClient.invalidateQueries({ queryKey: ['company', 'prepaid'] });
              setReopenOpen(false);
              snackbar.success('Atendimento reaberto.');
            }}
          />
        ) : null}
      </div>
    </section>
  );
}

/// A ficha do serviço agendado, que "Atualizar ficha" da checagem abre no painel lateral.
function serviceRecordTemplate(chart: ClientChart, session: AppointmentSession): string | null {
  return serviceTemplatesOf(chart, session.appointment.serviceId)[0]?.id ?? null;
}

/// Desfazer a conclusão por engano, com motivo (ADR 0030, decisão 10).
function ReopenDialog({
  appointment,
  onOpenChange,
  onReopened,
}: {
  appointment: Appointment;
  onOpenChange: (open: boolean) => void;
  onReopened: () => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [requestError, setRequestError] = useState<string>();
  const [saving, setSaving] = useState(false);

  async function submit() {
    setRequestError(undefined);
    setSaving(true);
    try {
      await schedulingApi.reopenAppointment(appointment.id, reason);
      await onReopened();
    } catch (cause) {
      setRequestError(getApiError(cause));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onOpenChange={onOpenChange}
      title="Reabrir atendimento"
      description={`O atendimento volta a ${appointment.confirmedAt ? 'Confirmado' : 'Agendado'}. O material registrado e o protocolo aplicado continuam como estão.`}
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Manter concluído
          </Button>
          <Button
            loading={saving}
            disabled={reason.trim().length < 3}
            onClick={() => void submit()}
          >
            Reabrir
          </Button>
        </>
      }
    >
      <div className="grid gap-3">
        <Textarea
          label="Motivo"
          rows={3}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Por exemplo: concluído no atendimento errado"
        />
        {requestError ? <RequestAlert message={requestError} /> : null}
      </div>
    </Modal>
  );
}

function ageOf(birthDate: string): number {
  const [year, month, day] = birthDate.split('-').map(Number) as [number, number, number];
  const today = new Date();
  const had =
    today.getMonth() + 1 > month || (today.getMonth() + 1 === month && today.getDate() >= day);
  return today.getFullYear() - year - (had ? 0 : 1);
}
